import { ContractsEmployeesContractVersionFragment } from 'generated/resources/graphql'
import { sortBy } from 'lodash'
import type moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'

const sortContractVersionsByEffectiveOn = (
  models: ContractsEmployeesContractVersionFragment[]
): ContractsEmployeesContractVersionFragment[] => {
  return sortBy(models, (cv: ContractsEmployeesContractVersionFragment) => cv.effectiveOn)
}

// Returns the subset of `models` (which should be the contract versions for
// an employee) that were effective (aka "current") between `startDate` and
// `endDate`.
const filterContractVersionsByCurrentWithinDateRange = (
  models: ContractsEmployeesContractVersionFragment[],
  startDate: moment.Moment,
  endDate: moment.Moment
): ContractsEmployeesContractVersionFragment[] => {
  const sortedModels = sortContractVersionsByEffectiveOn(models)

  return sortedModels.filter((model, index) => {
    const effectiveOn = stringToDate(model.effectiveOn, 'backend')
    const nextVersion = sortedModels[index + 1]
    const nextEffectiveOn = nextVersion
      ? stringToDate(nextVersion.effectiveOn, 'backend')
      : undefined
    return (
      effectiveOn.isSameOrBefore(endDate, 'day') &&
      (index === sortedModels.length - 1 || // last one?
        nextEffectiveOn?.isAfter(startDate, 'day'))
    )
  })
}

// Returns the subset of `models` (which should be the contract versions for
// an employee) that were effective on the specified `date`. This subset will
// contain 1 contract version at the most.
export const filterContractVersionsByCurrent = (
  models: ContractsEmployeesContractVersionFragment[],
  date: moment.Moment
): ContractsEmployeesContractVersionFragment[] => {
  return filterContractVersionsByCurrentWithinDateRange(models, date, date)
}

export const getUpcomingContractVersions = (
  models: ContractsEmployeesContractVersionFragment[],
  date: moment.Moment
): ContractsEmployeesContractVersionFragment | null | undefined => {
  const future = models.filter((cv) => {
    const effectiveOn = stringToDate(cv.effectiveOn, 'backend')
    return effectiveOn.isAfter(date)
  })

  return sortContractVersionsByEffectiveOn(future)[0]
}
